/**
 * AchievementStandardPicker — 성취기준 찾기 모달 (docs/achievement_standards_ux.md D3~D10)
 *
 * 위에서 아래로: 제목·설명 → 학교급 세그먼트(초등/중등/고등, 폼 상태와 하나로 동기화) · 맥락 배지 → 검색
 *   → 교육과정 사실 문장(+변경 버튼 1개)
 *   → 초등: 학년군 → 교과 / 중·고: 교과군(고등은 맨 끝 "전문교과" 칩) → 과목 → 영역 탭 + 체크 목록
 *   → 하단 고정: 고른 코드 칩 + "N개 넣기".
 * 학교급: 부모(`schoolLevel` + `onSchoolLevelChange`)가 단일 상태다. 세그먼트를 누르면 부모 상태가 바뀌고,
 *   바뀐 prop을 받아 과목·검색만 다시 시작한다(고른 성취기준은 유지). `onSchoolLevelChange`가 없으면 라벨만 보인다.
 * 폼 과목 칩(`initialSubjectLabel`)이 있으면 catalog의 form_subject_map으로 과목을 미리 고른다.
 * 검색(계약 v1.4, 사용자 피드백 9/10 14:38): 과목을 고르지 않아도 한 검색창에서 모든 학교급 × 2022/2015 ×
 *   보통/전문교과를 서버에서 한 번에 찾는다. 코드 정확 일치 → 현재 과목 → 현재 학교급 순으로 위에 오고,
 *   항목마다 학교급·과목을 함께 보인다. Enter는 정확 일치(또는 결과 1건)를 바로 담는다.
 * 교육과정: 2022/2015 토글 없이 학교급·학년(grade)으로 정하고, 전환이 있는 학년도에만 사실 문장으로 바꿀 수 있다.
 *   교사가 바꾼 값은 학교급·학년도별로 기억한다(`achievementStandardRecents`).
 * 삽입: onInsert(selectedRecords[]) → 부모가 textarea append + selectedStandards 병합.
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { searchStandards } from '../../api/curriculumApi';
import useCurriculumStandards from '../../hooks/useCurriculumStandards';
import Dialog, { useSheetLayout } from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import { Skeleton } from '../common/AsyncState';
import {
  DEFAULT_CURRICULUM,
  SCHOOL_LEVEL_SEGMENTS,
  SPECIAL_CURRICULUM,
  buildDomainTabs,
  buildSubjectGroups,
  capSelection,
  activeEditionFor,
  curriculumNotice,
  editionsOf,
  filterRecordsByEdition,
  curriculumTransitionFor,
  defaultCurriculumFor,
  domainIdOf,
  flattenSubjectStandards,
  getSchoolLevelEntry,
  isCurriculum2015,
  isLocatorCode,
  isSpecialCurriculum,
  mapFormSchoolLevel,
  normalizeCode,
  rankSearchResults,
  recordCurriculum,
  recordIdentity,
  resolveSubjectFromCatalog,
  schoolLevelLabel,
  schoolYearFromDate,
  toRecordSummary,
} from './achievementStandardPicker.utils';
import {
  RECENT_STANDARDS_LIMIT,
  readCurriculumPreference,
  readRecentStandards,
  readRecentSubjects,
  readSpecialPreference,
  rememberCurriculumPreference,
  rememberRecentStandards,
  rememberRecentSubject,
  rememberSpecialPreference,
} from './achievementStandardRecents';
import './AchievementStandardPicker.css';

const DEFAULT_MAX_SELECT = 10;
const DEFAULT_ELEMENTARY_BAND = '3-4';
const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_LIMIT = 50;
const EMPTY_KEYS = [];
const RECENT_STANDARDS_LIMIT_SHEET = 4;
const RECENT_TEXT_LIMIT = 18;

const LOCATOR_TITLE = '2015 전문교과Ⅱ는 고시에 성취기준 코드가 없어요. 이 번호는 찾기용이라 넣을 때는 원문만 들어가요';

const hasGrade = (grade) => grade !== null && grade !== undefined && grade !== '';

const truncateRecentText = (text) => {
  const value = String(text || '').trim();
  return value.length > RECENT_TEXT_LIMIT ? `${value.slice(0, RECENT_TEXT_LIMIT)}…` : value;
};

/**
 * 처음 열 때의 교육과정(설계 D4).
 * 특수 기본 교육과정을 켜 둔 교사는 그대로. 초등·전환 없는 학년도 → 2022.
 * 학년이 넘어오면 학년으로. 아니면 교사가 전에 바꾼 값, 없으면 2022.
 */
const resolveInitialCurriculum = ({
  grade,
  levelId,
  schoolLevel,
  schoolYear,
  useSpecialPreference = true,
}) => {
  if (useSpecialPreference && readSpecialPreference()) return SPECIAL_CURRICULUM;
  if (!curriculumTransitionFor({ schoolLevel, schoolYear })) return DEFAULT_CURRICULUM;
  if (hasGrade(grade)) return defaultCurriculumFor({ grade, schoolLevel, schoolYear });
  return readCurriculumPreference({ levelId, schoolYear }) || DEFAULT_CURRICULUM;
};

const AchievementStandardPicker = ({
  grade,
  initialSelectedKeys = EMPTY_KEYS,
  initialSubjectLabel,
  isOpen,
  maxSelect = DEFAULT_MAX_SELECT,
  onClose,
  onInsert,
  onSchoolLevelChange,
  schoolLevel,
  schoolYear,
}) => {
  const schoolLevelId = mapFormSchoolLevel(schoolLevel) || 'middle';
  const isElementary = schoolLevelId === 'elementary';
  const isHigh = schoolLevelId === 'high';
  const resolvedSchoolYear = schoolYear == null ? schoolYearFromDate() : Number(schoolYear);

  const [courseTrack, setCourseTrack] = useState('general');
  const [curriculum, setCurriculum] = useState(() => resolveInitialCurriculum({
    grade,
    levelId: schoolLevelId,
    schoolLevel,
    schoolYear: resolvedSchoolYear,
  }));
  const [gradeBand, setGradeBand] = useState(DEFAULT_ELEMENTARY_BAND);
  const [groupId, setGroupId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [activeDomain, setActiveDomain] = useState(null);
  const [limitHint, setLimitHint] = useState(false);
  const [remoteResults, setRemoteResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [flash, setFlash] = useState('');
  // 통합교과처럼 판이 둘인 과목에서 교사가 고른 판(과목 id → edition). 없으면 학년도로 고른다.
  const [editionOverrides, setEditionOverrides] = useState({});
  const editionOverridesRef = useRef({});
  editionOverridesRef.current = editionOverrides;
  const appliedInitialRef = useRef(false);
  const dismissedKeysRef = useRef(new Set());
  const searchInputRef = useRef(null);
  // Enter를 눌렀는데 서버 검색이 아직 진행 중이면, 결과가 도착한 직후 같은 규칙으로 담는다(설계 D6-1)
  const pendingQuickAddRef = useRef(null);
  // 효과 안에서 최신 props/선택/함수를 읽기 위한 거울(효과 의존성을 늘리지 않고 stale closure도 피한다)
  const latestRef = useRef({});
  latestRef.current = {
    grade,
    schoolLevel,
    schoolLevelId,
    schoolYear: resolvedSchoolYear,
  };
  const selectedRef = useRef([]);
  selectedRef.current = selected;
  const quickAddRef = useRef(null);
  const seenLevelRef = useRef(schoolLevelId);
  const idPrefix = useId();
  const isSheet = useSheetLayout(Boolean(isOpen));

  const catalogLevelId = isHigh && courseTrack === 'vocational' ? 'vocational' : schoolLevelId;
  const bandFilter = isElementary ? gradeBand : undefined;
  const notice = curriculumNotice({ curriculum, schoolLevel, schoolYear: resolvedSchoolYear });

  const {
    catalog,
    catalogError,
    loading,
    retry,
    subject,
    subjectError,
  } = useCurriculumStandards({
    curriculum,
    enabled: Boolean(isOpen),
    subjectId,
  });

  // 부모가 매 렌더마다 새 배열을 넘겨도 내용이 같으면 효과를 다시 돌리지 않는다.
  const initialSelectedKeySig = (initialSelectedKeys || EMPTY_KEYS).join('\n');
  const initialKeys = useMemo(
    () => (initialSelectedKeySig ? initialSelectedKeySig.split('\n') : EMPTY_KEYS),
    [initialSelectedKeySig],
  );

  /** 과목·검색 상태를 처음으로 되돌린다. 열 때는 선택도 비우고, 학교급만 바뀌면 선택은 남긴다. */
  const resetBrowse = useCallback(({ keepSelection }) => {
    const latest = latestRef.current;
    appliedInitialRef.current = false;
    setCourseTrack('general');
    setCurriculum(resolveInitialCurriculum({
      grade: latest.grade,
      levelId: latest.schoolLevelId,
      schoolLevel: latest.schoolLevel,
      schoolYear: latest.schoolYear,
    }));
    setGradeBand(DEFAULT_ELEMENTARY_BAND);
    setGroupId(null);
    setSubjectId(null);
    setQuery('');
    setActiveDomain(null);
    setRemoteResults([]);
    setSearching(false);
    setFlash('');
    setEditionOverrides({});
    pendingQuickAddRef.current = null;
    if (!keepSelection) {
      dismissedKeysRef.current = new Set();
      setSelected([]);
      setLimitHint(false);
    }
  }, []);

  // 열고 닫을 때 전부 초기화
  useEffect(() => {
    seenLevelRef.current = latestRef.current.schoolLevelId;
    resetBrowse({ keepSelection: false });
  }, [isOpen, resetBrowse]);

  // 열린 채로 학교급이 바뀌면(세그먼트 또는 폼) 과목·검색만 다시 시작한다
  useEffect(() => {
    if (!isOpen || seenLevelRef.current === schoolLevelId) return;
    seenLevelRef.current = schoolLevelId;
    resetBrowse({ keepSelection: true });
  }, [isOpen, resetBrowse, schoolLevelId]);

  // 폼 과목 칩 → 과목 사전 선택 (catalog가 준비된 뒤 한 번만)
  useEffect(() => {
    if (!isOpen || !catalog || appliedInitialRef.current) return;
    appliedInitialRef.current = true;
    const preferred = resolveSubjectFromCatalog(catalog, {
      formLabel: initialSubjectLabel,
      gradeBand: isElementary ? DEFAULT_ELEMENTARY_BAND : undefined,
      schoolLevelId: catalogLevelId,
    });
    if (!preferred) return;
    if (isElementary && preferred.grade_band) setGradeBand(preferred.grade_band);
    setGroupId(preferred.groupId || null);
    setSubjectId(preferred.id);
  }, [catalog, catalogLevelId, initialSubjectLabel, isElementary, isOpen]);

  const allStandards = useMemo(() => flattenSubjectStandards(subject), [subject]);
  const subjectEditions = useMemo(() => editionsOf(allStandards), [allStandards]);
  const activeEdition = subjectEditions.length > 0
    ? (subjectEditions.some((entry) => entry.edition === editionOverrides[subjectId])
      ? editionOverrides[subjectId]
      : activeEditionFor(subjectEditions, resolvedSchoolYear))
    : null;
  const shownStandards = useMemo(
    () => filterRecordsByEdition(allStandards, resolvedSchoolYear, editionOverrides),
    [allStandards, editionOverrides, resolvedSchoolYear],
  );

  // 이미 삽입된 성취기준은 미리 체크해 둔다(사용자가 이 세션에서 뺀 것은 다시 채우지 않는다).
  // 다른 판에서 넣었던 것이면 그 판을 보여 준다(목록에 안 보이는 채로 다른 판을 또 담지 않게).
  useEffect(() => {
    if (!isOpen || initialKeys.length === 0 || allStandards.length === 0) return;
    const matches = allStandards.filter((record) => {
      const identity = recordIdentity(toRecordSummary(record, curriculum));
      return (
        (initialKeys.includes(identity) || initialKeys.includes(record.key))
        && !dismissedKeysRef.current.has(identity)
      );
    });
    if (matches.length === 0) return;
    const editions = editionsOf(allStandards);
    if (editions.length > 0) {
      const active = activeEditionFor(editions, latestRef.current.schoolYear);
      const other = matches.find((record) => (
        record.subject_id
        && record.edition !== active
        && editions.some((entry) => entry.edition === record.edition)
      ));
      if (other) {
        setEditionOverrides((prev) => (
          prev[other.subject_id] ? prev : { ...prev, [other.subject_id]: other.edition }
        ));
      }
    }
    setSelected((current) => {
      const currentIds = new Set(current.map(recordIdentity));
      const hydrated = matches
        .map((record) => toRecordSummary(record, curriculum))
        .filter((summary) => !currentIds.has(recordIdentity(summary)));
      return hydrated.length > 0 ? [...current, ...hydrated] : current;
    });
  }, [allStandards, curriculum, initialKeys, isOpen]);

  // 영역 탭(계약 v1.2: domain null 한 그룹이면 탭 없이 평면 목록). 고른 영역이 이 과목에 없으면 첫 영역 —
  // 효과 대신 파생값이라 깜빡임이 없다.
  const { flat: flatDomains, tabs: domainTabs } = useMemo(() => buildDomainTabs(subject), [subject]);
  const effectiveDomain = flatDomains
    ? null
    : (domainTabs.some((tab) => tab.id === activeDomain) ? activeDomain : (domainTabs[0]?.id || null));

  const trimmedQuery = query.trim();

  /**
   * Enter 즉시 담기: 정규화 코드가 정확히 같은 항목, 없으면 결과가 하나일 때 그 항목. 담으면 검색어를 비운다.
   * 같은 코드가 2022·2015에 다 있으면 선택기가 보고 있는 교육과정(사실 문장의 현재 값)을 담고,
   * 그 교육과정에 없을 때만 다른 교육과정을 담는다(2026 중3 교사가 2015 원문을 기대하는데 2022가 들어가면 안 된다).
   */
  /** 빠진 항목(직접 뺐거나 다른 판으로 바뀐 것)은 미리 체크 효과가 다시 채우지 않게 기억한다. */
  const dismissReplaced = (before, after) => {
    const kept = new Set(after.map(recordIdentity));
    before.forEach((record) => {
      const identity = recordIdentity(record);
      if (!kept.has(identity)) dismissedKeysRef.current.add(identity);
    });
  };

  const quickAdd = (candidates, rawQuery) => {
    const queryCode = normalizeCode(rawQuery);
    const exactMatches = queryCode
      ? candidates.filter((record) => (record.code_norm || normalizeCode(record.code)) === queryCode)
      : [];
    const exact = exactMatches.find((record) => recordCurriculum(record) === curriculum) || exactMatches[0] || null;
    const target = exact || (candidates.length === 1 ? candidates[0] : null);
    if (!target) return false;
    const stamped = toRecordSummary(target, curriculum);
    const identity = recordIdentity(stamped);
    const current = selectedRef.current;
    if (current.some((record) => recordIdentity(record) === identity)) {
      setFlash(`${stamped.code}는 이미 담았어요`);
    } else {
      const next = capSelection(current, stamped, maxSelect);
      dismissReplaced(current, next.records);
      setSelected(next.records);
      setLimitHint(next.limited);
      if (!next.limited) {
        dismissedKeysRef.current.delete(identity);
        setFlash(`${stamped.code} 담았어요`);
      }
    }
    setQuery('');
    return true;
  };
  quickAddRef.current = quickAdd;

  // 전체 서버 검색(디바운스). 현재 교육과정·학교급·과목은 필터가 아니라 정렬 힌트로만 보낸다.
  useEffect(() => {
    if (!isOpen || !trimmedQuery) {
      setRemoteResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      const result = await searchStandards(trimmedQuery, {
        limit: SEARCH_LIMIT,
        preferCurriculum: curriculum,
        preferSchoolLevel: catalogLevelId,
        preferSubjectId: subjectId || undefined,
      });
      if (cancelled) return;
      const results = filterRecordsByEdition(
        rankSearchResults(
          result.ok ? (result.data?.results || []) : [],
          {
            curriculum,
            query: trimmedQuery,
            schoolLevelId: catalogLevelId,
            subjectId,
          },
        ),
        latestRef.current.schoolYear,
        editionOverridesRef.current,
      );
      setSearching(false);
      setRemoteResults(results);
      if (pendingQuickAddRef.current === trimmedQuery) {
        pendingQuickAddRef.current = null;
        quickAddRef.current?.(results, trimmedQuery);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [catalogLevelId, curriculum, isOpen, subjectId, trimmedQuery]);

  const levelEntry = getSchoolLevelEntry(catalog, catalogLevelId);
  const groups = useMemo(
    () => buildSubjectGroups(catalog, catalogLevelId, { gradeBand: bandFilter }),
    [bandFilter, catalog, catalogLevelId],
  );
  // 고등 교과군 행 맨 끝의 "전문교과" 칩은 카탈로그에 전문교과가 있을 때만(설계 D5)
  const hasVocational = isHigh && buildSubjectGroups(catalog, 'vocational').length > 0;
  const activeGroup = groups.find((group) => group.id === groupId) || null;

  // 이 학교급(학년군 무관)의 모든 과목. 최근 과목 검증과 삽입 시 과목 기억에 쓴다.
  const subjectsById = useMemo(() => {
    const map = new Map();
    buildSubjectGroups(catalog, catalogLevelId).forEach((group) => {
      group.subjects.forEach((item) => map.set(item.id, item));
    });
    return map;
  }, [catalog, catalogLevelId]);
  const selectedSubjectEntry = useMemo(() => {
    if (!subjectId) return null;
    for (const group of groups) {
      const found = group.subjects.find((item) => item.id === subjectId);
      if (found) return found;
    }
    return null;
  }, [groups, subjectId]);

  const visibleStandards = useMemo(() => {
    if (trimmedQuery) return remoteResults;
    if (effectiveDomain) {
      return shownStandards.filter((record) => domainIdOf(record.domain) === effectiveDomain);
    }
    return shownStandards;
  }, [effectiveDomain, remoteResults, shownStandards, trimmedQuery]);

  const selectedIds = useMemo(
    () => new Set(selected.map(recordIdentity)),
    [selected],
  );

  // 헤비유저 지름길(설계 D6): 쓴 적이 있을 때만 나타난다. 카탈로그에 아직 있는 과목만 보인다.
  const recentSubjects = useMemo(() => {
    if (!isOpen || !catalog) return [];
    return readRecentSubjects({ curriculum, levelId: catalogLevelId })
      .map((entry) => subjectsById.get(entry.id))
      .filter(Boolean);
  }, [catalog, catalogLevelId, curriculum, isOpen, subjectsById]);

  // 과목 안에서는 그 과목에 실제로 있는 것만, 과목 전에는 학교급·교육과정 기준으로.
  const recentStandardsLimit = isSheet ? RECENT_STANDARDS_LIMIT_SHEET : RECENT_STANDARDS_LIMIT;
  const recentStandards = useMemo(() => {
    if (!isOpen) return [];
    if (!subjectId) {
      return readRecentStandards({ curriculum, levelId: catalogLevelId, limit: recentStandardsLimit });
    }
    if (allStandards.length === 0) return [];
    const present = new Set(allStandards.map((record) => recordIdentity(toRecordSummary(record, curriculum))));
    return readRecentStandards({
      curriculum,
      levelId: catalogLevelId,
      limit: RECENT_STANDARDS_LIMIT * 4,
      subjectId,
    })
      .filter((record) => present.has(recordIdentity(record)))
      .slice(0, recentStandardsLimit);
  }, [allStandards, catalogLevelId, curriculum, isOpen, recentStandardsLimit, subjectId]);

  const pickSubject = (nextSubjectId, nextGroupId) => {
    setGroupId(nextGroupId || null);
    setSubjectId(nextSubjectId || null);
    setActiveDomain(null);
    setQuery('');
  };

  const handleSchoolLevel = (segment) => {
    if (segment.id === schoolLevelId || !onSchoolLevelChange) return;
    // 부모 상태만 바꾼다. 바뀐 prop이 내려오면 위 효과가 과목·검색을 다시 시작한다(단일 상태).
    onSchoolLevelChange(segment.formLabel);
  };

  const handleTrackChange = (track) => {
    if (track === courseTrack) return;
    appliedInitialRef.current = true;
    setCourseTrack(track);
    pickSubject(null, null);
  };

  const handleCurriculumChange = (nextCurriculum) => {
    if (nextCurriculum === curriculum) return;
    appliedInitialRef.current = false;
    pickSubject(null, null);
    setCurriculum(nextCurriculum);
    rememberCurriculumPreference({
      curriculum: nextCurriculum,
      levelId: schoolLevelId,
      schoolYear: resolvedSchoolYear,
    });
  };

  const isSpecial = curriculum === SPECIAL_CURRICULUM;
  const handleSpecialToggle = () => {
    const next = isSpecial
      ? resolveInitialCurriculum({
        grade,
        levelId: schoolLevelId,
        schoolLevel,
        schoolYear: resolvedSchoolYear,
        useSpecialPreference: false,
      })
      : SPECIAL_CURRICULUM;
    rememberSpecialPreference(!isSpecial);
    appliedInitialRef.current = false;
    setCourseTrack('general');
    pickSubject(null, null);
    setCurriculum(next);
  };

  const handleGradeBand = (band) => {
    setGradeBand(band);
    const preferred = resolveSubjectFromCatalog(catalog, {
      formLabel: initialSubjectLabel,
      gradeBand: band,
      schoolLevelId: 'elementary',
    });
    pickSubject(preferred?.id || null, preferred?.groupId || null);
  };

  const handleGroup = (group) => {
    if (group.subjects.length === 1) {
      pickSubject(group.subjects[0].id, group.id);
      return;
    }
    pickSubject(null, group.id);
  };

  const handleBack = () => {
    const keepGroup = activeGroup && activeGroup.subjects.length > 1;
    pickSubject(null, keepGroup ? activeGroup.id : null);
  };

  const handleToggle = (record) => {
    const stamped = toRecordSummary(record, curriculum);
    const next = capSelection(selected, stamped, maxSelect);
    dismissReplaced(selected, next.records);
    setSelected(next.records);
    setLimitHint(next.limited);
    setFlash('');
  };

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter' || !trimmedQuery) return;
    event.preventDefault();
    if (searching) {
      pendingQuickAddRef.current = trimmedQuery;
      return;
    }
    quickAdd(visibleStandards, trimmedQuery);
  };

  const handleRecentSubject = (entry) => {
    if (isElementary && entry.grade_band) setGradeBand(entry.grade_band);
    pickSubject(entry.id, entry.groupId);
  };

  const handleInsert = () => {
    if (selected.length === 0) return;
    rememberRecentStandards({ levelId: catalogLevelId, records: selected });
    const remembered = new Set();
    selected.forEach((record) => {
      const entry = record.subject_id ? subjectsById.get(record.subject_id) : null;
      if (!entry || remembered.has(entry.id)) return;
      remembered.add(entry.id);
      rememberRecentSubject({ curriculum, levelId: catalogLevelId, subject: entry });
    });
    onInsert?.(selected);
    onClose?.();
  };

  const browsing = !trimmedQuery;
  const choosingSubject = browsing && !subjectId;
  const showSubjectChips = choosingSubject && Boolean(activeGroup) && activeGroup.subjects.length > 1;
  const subjectLabel = subject?.subject?.label || selectedSubjectEntry?.label || '과목';
  const showList = !loading && !searching && !catalogError && !subjectError && visibleStandards.length > 0;
  const showEmpty = !loading && !searching && !catalogError && !subjectError
    && visibleStandards.length === 0 && Boolean(subjectId || trimmedQuery);
  const showNoSubjects = !loading && !catalogError && Boolean(catalog) && choosingSubject && groups.length === 0;
  const showGroupsRow = !catalogError && choosingSubject && !loading
    && (groups.length > 0 || (isHigh && courseTrack === 'vocational'));
  const showRecentSubjects = !catalogError && choosingSubject && !loading && recentSubjects.length > 0;
  const showRecentStandards = !catalogError && !subjectError && browsing && !loading && recentStandards.length > 0;
  const insertLabel = selected.length > 0 ? `${selected.length}개 넣기` : '넣기';
  const liveMessage = flash || (selected.length > 0 ? `${selected.length}개 골랐어요` : '');

  if (!isOpen) return null;

  const renderStatus = (error, testId) => (
    <div className="asp__status" data-testid={testId} role="alert">
      <p>{error.message}</p>
      <button className="asp__retry" onClick={retry} type="button">
        다시 시도
      </button>
    </div>
  );

  const renderRecentStandards = () => (
    <div aria-label="최근 성취기준" className="asp__row asp__row--recent" data-testid="asp-recent-standards" role="group">
      <span className="asp__row-label">최근</span>
      {recentStandards.map((record) => {
        const checked = selectedIds.has(recordIdentity(record));
        return (
          <button
            aria-pressed={checked}
            className={`asp__recent ${checked ? 'is-active' : ''}`}
            data-testid={`asp-recent-standard-${normalizeCode(record.code)}`}
            key={recordIdentity(record)}
            onClick={() => handleToggle(record)}
            title={`${record.code} ${record.text || ''}`.trim()}
            type="button"
          >
            <b>{record.code}</b>
            {' '}
            <span className="asp__recent-text">{truncateRecentText(record.text)}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <Dialog
      closeLabel="성취기준 찾기 닫기"
      description="고른 성취기준은 교육과정 원문 그대로 들어가요"
      dragHandle
      footer={(
        <div className="asp-footer">
          {selected.length > 0 && (
            <div className="asp-chips" data-testid="asp-selected">
              {selected.map((record) => (
                <span className="asp-chip" key={recordIdentity(record)}>
                  {record.code}
                  {isCurriculum2015(record) && (
                    <span className="asp-curriculum-badge" data-testid="asp-chip-curriculum-2015">2015</span>
                  )}
                  {isSpecialCurriculum(record) && (
                    <span className="asp-curriculum-badge" data-testid="asp-chip-curriculum-special">특수</span>
                  )}
                  {isLocatorCode(record) && (
                    <span className="asp-curriculum-badge asp-code-kind-badge" data-testid="asp-chip-locator" title={LOCATOR_TITLE}>
                      고시 코드 아님
                    </span>
                  )}
                  <button
                    aria-label={`${record.code} 빼기`}
                    className="asp-chip__remove"
                    onClick={() => handleToggle(record)}
                    type="button"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <ModalActions
            primary={{
              disabled: selected.length === 0,
              label: insertLabel,
              onClick: handleInsert,
              testId: 'asp-insert',
            }}
          />
        </div>
      )}
      initialFocus={isSheet ? 'panel' : searchInputRef}
      onClose={onClose}
      open
      panelClassName="asp-dialog"
      size="lg"
      testId="asp-picker"
      title="성취기준 찾기"
    >
      <div className="asp">
        <p aria-live="polite" className="whatquiz-visually-hidden" data-testid="asp-live">{liveMessage}</p>

        <div className="asp__meta">
          {onSchoolLevelChange ? (
            <div aria-label="학교급" className="asp__levels" data-testid="asp-school-levels" role="group">
              {SCHOOL_LEVEL_SEGMENTS.map((segment) => (
                <button
                  aria-pressed={segment.id === schoolLevelId}
                  className={`asp__level-btn ${segment.id === schoolLevelId ? 'is-active' : ''}`}
                  data-testid={`asp-level-${segment.id}`}
                  key={segment.id}
                  onClick={() => handleSchoolLevel(segment)}
                  type="button"
                >
                  {segment.formLabel}
                </button>
              ))}
            </div>
          ) : (
            <span className="asp__level" data-testid="asp-school-level">
              {schoolLevelLabel(schoolLevelId) || schoolLevel}
            </span>
          )}
          {catalogLevelId === 'vocational' && (
            <span className="asp__badge" data-testid="asp-track-badge">전문교과</span>
          )}
          {notice && (
            <span className="asp__badge" data-testid="asp-curriculum-badge">{`${curriculum} 개정`}</span>
          )}
          <button
            aria-pressed={isSpecial}
            className={`asp__special-btn ${isSpecial ? 'is-active' : ''}`}
            data-testid="asp-special-toggle"
            onClick={handleSpecialToggle}
            title="2022 개정 특수교육 기본 교육과정(교육부 고시 제2022-34호)"
            type="button"
          >
            특수 기본 교육과정
          </button>
        </div>

        <label className="asp__search">
          <span className="whatquiz-visually-hidden">성취기준 찾기</span>
          <input
            className="asp__search-input"
            data-testid="asp-search"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="코드·내용으로 모든 학교급에서 찾기"
            ref={searchInputRef}
            type="search"
            value={query}
          />
        </label>

        {notice && (
          <p className="asp__notice" data-testid="asp-curriculum-notice">
            <span className="asp__notice-text">{notice.sentence}</span>
            <button
              className="asp__notice-btn"
              data-testid="asp-curriculum-switch"
              onClick={() => handleCurriculumChange(notice.switchTo)}
              type="button"
            >
              {notice.switchLabel}
            </button>
          </p>
        )}

        {subjectId && !trimmedQuery && activeEdition && (() => {
          const current = subjectEditions.find((entry) => entry.edition === activeEdition);
          const other = subjectEditions.find((entry) => entry.edition !== activeEdition);
          if (!current || !other) return null;
          const isNewer = String(other.effectiveFrom) > String(current.effectiveFrom);
          const otherInEffect = other.edition === activeEditionFor(subjectEditions, resolvedSchoolYear);
          let switchLabel = isNewer ? `${other.schoolYear}학년도부터 쓰는 개정판 보기` : '이전 판 보기';
          if (otherInEffect) switchLabel = '지금 쓰는 판 보기';
          return (
            <p className="asp__notice" data-testid="asp-edition-notice">
              <span className="asp__notice-text">
                {`${current.notice || current.edition} 기준이에요`}
              </span>
              <button
                className="asp__notice-btn"
                data-testid="asp-edition-switch"
                onClick={() => setEditionOverrides((prev) => ({ ...prev, [subjectId]: other.edition }))}
                type="button"
              >
                {switchLabel}
              </button>
            </p>
          );
        })()}

        {catalogError && renderStatus(
          catalogError,
          catalogError.code === 'unavailable' ? 'asp-unavailable' : 'asp-error',
        )}

        {showRecentStandards && !subjectId && renderRecentStandards()}

        {showRecentSubjects && (
          <div aria-label="최근 과목" className="asp__row asp__row--recent" data-testid="asp-recent-subjects" role="group">
            <span className="asp__row-label">최근 과목</span>
            {recentSubjects.map((entry) => (
              <button
                className="asp__chip"
                data-testid={`asp-recent-subject-${entry.id}`}
                key={entry.id}
                onClick={() => handleRecentSubject(entry)}
                type="button"
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}

        {!catalogError && choosingSubject && isElementary && !loading && (
          <div aria-label="학년군" className="asp__row" data-testid="asp-grade-bands" role="group">
            {(levelEntry?.grade_bands || []).map((band) => (
              <button
                aria-pressed={gradeBand === band.id}
                className={`asp__chip ${gradeBand === band.id ? 'is-active' : ''}`}
                data-testid={`asp-grade-band-${band.id}`}
                key={band.id}
                onClick={() => handleGradeBand(band.id)}
                type="button"
              >
                {band.label}
              </button>
            ))}
          </div>
        )}

        {showGroupsRow && (
          <div
            aria-label={isElementary ? '교과' : '교과군'}
            className="asp__row"
            data-testid="asp-groups"
            role="group"
          >
            {catalogLevelId === 'vocational' && (
              <button
                aria-label="보통교과로 돌아가기"
                className="asp__back"
                data-testid="asp-track-general"
                onClick={() => handleTrackChange('general')}
                type="button"
              >
                <span aria-hidden="true">‹</span>
                {' '}
                보통교과
              </button>
            )}
            {groups.map((group) => (
              <button
                aria-pressed={groupId === group.id}
                className={`asp__chip ${groupId === group.id ? 'is-active' : ''}`}
                data-testid={`asp-group-${group.id}`}
                key={group.id}
                onClick={() => handleGroup(group)}
                type="button"
              >
                {group.label}
              </button>
            ))}
            {hasVocational && catalogLevelId !== 'vocational' && (
              <button
                className="asp__chip asp__chip--more"
                data-testid="asp-track-vocational"
                onClick={() => handleTrackChange('vocational')}
                type="button"
              >
                전문교과
                {' '}
                <span aria-hidden="true">›</span>
              </button>
            )}
          </div>
        )}

        {!catalogError && showSubjectChips && !loading && (
          <div aria-label="과목" className="asp__row asp__row--sub" data-testid="asp-subjects" role="group">
            {activeGroup.subjects.map((item) => (
              <button
                className="asp__chip"
                data-testid={`asp-subject-${item.id}`}
                key={item.id}
                onClick={() => pickSubject(item.id, activeGroup.id)}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {showNoSubjects && (
          <p className="asp__empty" data-testid="asp-no-subjects">아직 준비된 과목이 없어요</p>
        )}

        {!catalogError && subjectId && (
          <div
            aria-label="영역"
            className="asp__row"
            data-testid={catalogLevelId === 'vocational' ? 'asp-vocational-tree' : 'asp-domains'}
            role="group"
          >
            <button
              aria-label={`${subjectLabel} — 과목 다시 고르기`}
              className="asp__back"
              data-testid="asp-back"
              onClick={handleBack}
              type="button"
            >
              <span aria-hidden="true">‹</span>
              {' '}
              {subjectLabel}
            </button>
            {browsing && !loading && domainTabs.map((tab) => (
              <button
                aria-pressed={effectiveDomain === tab.id}
                className={`asp__domain ${effectiveDomain === tab.id ? 'is-active' : ''}`}
                data-testid={`asp-domain-${tab.label}`}
                key={tab.id}
                onClick={() => setActiveDomain(tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {!catalogError && subjectError && renderStatus(
          subjectError,
          subjectError.code === 'unavailable' ? 'asp-unavailable' : 'asp-error',
        )}

        {showRecentStandards && Boolean(subjectId) && renderRecentStandards()}

        {(loading || searching) && !catalogError && (
          <div aria-busy="true" aria-label="불러오는 중" className="asp-skeleton" data-testid="asp-skeleton" role="status">
            <Skeleton height={44} variant="block" />
            <Skeleton lines={3} />
          </div>
        )}

        {limitHint && (
          <p className="asp__limit" data-testid="asp-limit-hint" role="status">
            {maxSelect}개까지 고를 수 있어요
          </p>
        )}

        {showEmpty && (
          <p className="asp__empty" data-testid="asp-empty">해당하는 성취기준이 없어요</p>
        )}

        {showList && (
          <div className="asp__list" data-testid="asp-list">
            {visibleStandards.map((record, index) => {
              const stamped = toRecordSummary(record, curriculum);
              const checked = selectedIds.has(recordIdentity(stamped));
              const inputId = `${idPrefix}-${index}`;
              const levelLabel = schoolLevelLabel(record.school_level);
              return (
                <div
                  className={`asp-item ${checked ? 'is-checked' : ''}`}
                  data-testid={`asp-standard-${record.code_norm || normalizeCode(record.code)}`}
                  key={recordIdentity(stamped)}
                >
                  <input
                    checked={checked}
                    className="asp-item__check"
                    id={inputId}
                    onChange={() => handleToggle(record)}
                    type="checkbox"
                  />
                  <label className="asp-item__body" htmlFor={inputId}>
                    <span className="asp-item__code">
                      <span>{record.code}</span>
                      {!browsing && (levelLabel || record.subject) && (
                        <span className="asp-item__subject" data-testid="asp-item-scope">
                          {[levelLabel, record.subject].filter(Boolean).join(' ')}
                        </span>
                      )}
                      {!browsing && record.domain && (
                        <span className="asp-item__subject" data-testid="asp-item-domain">{record.domain}</span>
                      )}
                      {isCurriculum2015(stamped) && (
                        <span className="asp-curriculum-badge" data-testid="asp-item-curriculum-2015">2015</span>
                      )}
                      {isSpecialCurriculum(stamped) && (
                        <span className="asp-curriculum-badge" data-testid="asp-item-curriculum-special">특수</span>
                      )}
                      {isLocatorCode(stamped) && (
                        <span className="asp-curriculum-badge asp-code-kind-badge" data-testid="asp-item-locator" title={LOCATOR_TITLE}>
                          고시 코드 아님
                        </span>
                      )}
                    </span>
                    <span className="asp-item__text">{record.text}</span>
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default AchievementStandardPicker;
